//#region IMPORTS
import {
  setOrderParams,
  clearOrderParam,
  clearOrderParams,
} from "../../features/orders/ordersSlice";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { OrderParams } from "../../models/order";
import { ReactNode } from "react";
import MainFilterBarItem from "../_layout/MainFilterBarItem";
import usePickerManager from "../../hooks/usePickerManager";
import useAdminManager from "../../hooks/useAdminManager";
import MainFilterBar from "../_layout/MainFilterBar";
import useHQmanager from "../../hooks/useHQmanager";
//#endregion

//#region INTERFACE
interface Props {
  hideOrderstatusFilter?: boolean;
  hideOrdertypeFilter?: boolean;
  extensionButtons?: ReactNode;
  // Params the *view* owns (a promo preset's order_type / sort / limit).
  // "Clear all filters" resets to these instead of to the generic order
  // defaults, so it clears the user's filters without leaving the preset.
  baseParams?: Partial<OrderParams>;
}
//#endregion

export default function OrdersFilterBar({
  hideOrderstatusFilter = false,
  hideOrdertypeFilter = false,
  extensionButtons,
  baseParams,
}: Props) {
  //#region SETUP
  // Redux
  const dispatch = useAppDispatch();
  const { page, limit, sort, include_sub_orders, ...params } = useAppSelector(
    (state) => state.orders.order_params
  );
  const { filters, filters_loaded, loading } = useAppSelector(
    (state) => state.orders
  );
  const { user } = useAppSelector((state) => state.account);

  const isPicker = usePickerManager(user);
  const isAdmin = useAdminManager(user);
  const isHQ = useHQmanager(user);

  const paramsDisabledList = ["order_status", "order_type"].filter(
    (param) =>
      (param === "order_status" && hideOrderstatusFilter) ||
      (param === "order_type" && hideOrdertypeFilter)
  );

  //#endregion

  //#region RENDER
  return (
    <MainFilterBar
      paramNamesList={params}
      paramsDisabledList={paramsDisabledList}
      clearAllFiltersHandler={() => dispatch(clearOrderParams(baseParams))}
      extensionButtons={extensionButtons}
      clearFilterHandler={(paramName: string) => {
        dispatch(clearOrderParam(paramName));
      }}
      disabled={!filters_loaded || loading}
    >
      {/* order_code */}
      <MainFilterBarItem
        filterParameterName="order_code"
        displayExplanation="Search the orders by order code"
        clearFilterAction={() => dispatch(clearOrderParam("order_code"))}
        selectedValue={params.order_code}
        filterAction={(value: string) =>
          dispatch(setOrderParams({ order_code: value }))
        }
        displayText="Order code"
        selected={!!params.order_code}
      />
      {/* store_number */}
      <MainFilterBarItem
        filterParameterName="store_number"
        displayExplanation="Search the orders by store number"
        clearFilterAction={() => dispatch(clearOrderParam("store_number"))}
        selectedValue={params.store_number}
        filterAction={(value: string) =>
          dispatch(setOrderParams({ store_number: value }))
        }
        displayText="Store ID"
        selected={!!params.store_number}
        hide={!isAdmin && !isHQ && !isPicker}
      />
      {/* promo_code */}
      <MainFilterBarItem
        filterParameterName="promo_code"
        displayExplanation="Filter the orders by promo code"
        clearFilterAction={() => dispatch(clearOrderParam("promo_code"))}
        selectBoxItems={filters.promo_code.filter((item) => item !== null)}
        selectedValue={params.promo_code}
        filterAction={(value: string) => {
          dispatch(setOrderParams({ promo_code: value }));
        }}
        displayText="Promo code"
        selectBox
        selected={!!params.promo_code}
      />

      {/* city */}
      <MainFilterBarItem
        filterParameterName="city"
        displayExplanation="Filter the orders by city"
        clearFilterAction={() => dispatch(clearOrderParam("city"))}
        selectBoxItems={filters.city.filter((item) => item !== null)}
        selectedValue={params.city}
        filterAction={(value: string) => {
          dispatch(setOrderParams({ city: value }));
        }}
        displayText="City"
        selectBox
        selected={!!params.city}
        hide={!isAdmin && !isHQ && !isPicker}
      />

      {/* country_name */}
      <MainFilterBarItem
        filterParameterName="country_name"
        displayExplanation="Filter the orders by country name"
        clearFilterAction={() => dispatch(clearOrderParam("country_name"))}
        selectBoxItems={filters.country_name.filter((item) => item !== null)}
        selectedValue={params.country_name}
        filterAction={(value: string) => {
          dispatch(setOrderParams({ country_name: value }));
        }}
        displayText="Country name"
        selectBox
        selected={!!params.country_name}
        hide={!isAdmin && !isHQ && !isPicker}
      />

      {/* language */}
      <MainFilterBarItem
        filterParameterName="language"
        displayExplanation="Filter the orders by language"
        clearFilterAction={() => dispatch(clearOrderParam("language"))}
        selectBoxItems={filters.language.filter((item) => item !== null)}
        selectedValue={params.language}
        filterAction={(value: string) => {
          dispatch(setOrderParams({ language: value }));
        }}
        displayText="Language"
        selectBox
        selected={!!params.language}
        hide={!isAdmin && !isHQ && !isPicker}
      />

      {/* order_status */}
      <MainFilterBarItem
        filterParameterName="order_status"
        displayExplanation="Filter the orders by order status"
        clearFilterAction={() => dispatch(clearOrderParam("order_status"))}
        selectBoxItems={filters.order_status.filter((item) => item !== null)}
        selectedValue={params.order_status}
        filterAction={(value: string) => {
          dispatch(setOrderParams({ order_status: value }));
        }}
        displayText="Order status"
        selectBox
        selected={!!params.order_status}
        hide={hideOrderstatusFilter}
      />

      {/* order_type */}
      <MainFilterBarItem
        filterParameterName="order_type"
        displayExplanation="Filter the orders by order type"
        clearFilterAction={() => dispatch(clearOrderParam("order_type"))}
        selectBoxItems={filters.order_type.filter((item) => item !== null)}
        selectedValue={params.order_type}
        filterAction={(value: string) => {
          dispatch(setOrderParams({ order_type: value }));
        }}
        displayText="Order type"
        selectBox
        selected={!!params.order_type}
        hide={hideOrdertypeFilter}
      />
    </MainFilterBar>
  );
  //#endregion
}
